import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, Checkbox, Col, Input, Popover, Tag, Typography, Tooltip } from 'antd';
import Icon from '@ant-design/icons';
import Form from 'antd/es/form';
import { useWatch } from 'antd/es/form/Form';
import { FormListFieldData } from 'antd/es/form/FormList';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import clsx from 'clsx';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';
import moment from 'moment-timezone';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useContextNotifications } from '../../../../../../../context/notifications';
import { useContextReports } from '../../../../../../../context/reports';
import { EnumReportTypes, ILoanColumn, IReportRow } from '../../../../../../../types/reports';
import { getInputsCount } from '../../../../../../../utils/loan';
import { Delete, Drag, Duplicate, Note, Paint } from '../../../../../../Common/Icon';
import styles from '../../index.module.less';
import SubField from './SubField';
import Field from './Field';

interface ITableCol {
  field: FormListFieldData;
  provided?: DraggableProvided;
  isDragging: boolean;
  disabled: boolean;
  handleRemove: () => void;
  itemKey: number;
  onCheckAddUipToggle: (e: CheckboxChangeEvent, id: string | number) => void;
  onCheckAddVaFundingFeeToggle: (e: CheckboxChangeEvent, id: string | number) => void;
  onCheckAddClosingCostsToggle: (e: CheckboxChangeEvent, id: string | number) => void;
  onCheckAddCashOutToggle: (e: CheckboxChangeEvent, id: string | number) => void;
  onCheckboxRecalculate: (e: CheckboxChangeEvent, id: string, zpid: string) => void;
  onCheckboxInterestOnlyToggle: (e: CheckboxChangeEvent, id: string) => void;
  onCheckboxRemoveDownPaymentToggle: (e: CheckboxChangeEvent, id: string) => void;
  isFirst: boolean;
  isRefinance: boolean;
  reportType: EnumReportTypes | undefined;
  loanId: string | number | undefined;
}

// 4. Make mortgage insurance to have selection with options - 0.1, 0.15, 0.2, 0.25, 0.3, 0.55
const TableCol = ({
  itemKey,
  field,
  provided,
  isDragging,
  disabled,
  handleRemove,
  onCheckboxRecalculate,
  onCheckAddUipToggle,
  onCheckAddVaFundingFeeToggle,
  onCheckAddClosingCostsToggle,
  onCheckAddCashOutToggle,
  onCheckboxInterestOnlyToggle,
  onCheckboxRemoveDownPaymentToggle,
  isFirst,
  isRefinance,
  reportType,
}: ITableCol) => {
  const { openConfirm } = useContextNotifications();
  const {
    loans: reportLoans,
    isBorrower,
    handleLoansUpdate,
    report,
    getLoans,
    getReportClosingCosts,
    loansLoading
  } = useContextReports();
  const colorBlue = getComputedStyle(document.documentElement).getPropertyValue('--color-blue');
  const highlightColor = getComputedStyle(document.documentElement).getPropertyValue('--color-highlight-yellow');
  const [editingName, setEditingName] = useState<Record<number, boolean>>({});
  const [editingAddress, setEditingAddress] = useState<Record<number, boolean>>({});
  const uipAvailable = useWatch(['tableView', 'uip'])
  const vaFundingFeeAvailable = useWatch(['tableView', 'vaFundingFee', 'active'])
  const closingCostsAvailable = useWatch(['tableView', 'closingCosts', 'active'])
  const cashOutAvailable = useWatch(['tableView', 'cashOut', 'active'])
  const location = useLocation()

  const isPreview = location.pathname.includes('/preview');

  const colColorChange = (name: number) => {
    const color = form.getFieldValue(['loans', name, 'color']);

    form.setFieldValue(['loans', name, 'color'], color ? null : highlightColor);
    handleLoansUpdate?.();
  };

  const filterLoansWithSegment = (loans: ILoanColumn[]) => loans?.filter(() => report?.enableComparisonDisplay);

  const fieldColorChange = (index: number, name: string) => {
    const colors = form.getFieldValue(['loans', index, 'loanColor']);

    form.setFieldValue(['loans', index, 'loanColor'], {
      ...colors,
      [name]: colors?.[name] || colors?.[name] === '0' ? null : highlightColor,
    });

    handleLoansUpdate?.();
  };

  const form = useFormInstance();
  const loans = useWatch('loans', form);

  const handleClone = (key: number) => {
    const newLoans = [...loans];
    const originalLoan = { ...newLoans[key] };

    const originalLoanType = isBorrower ? 'borrower' : 'purchase';

    const newLoan = {
      ...JSON.parse(JSON.stringify(originalLoan)), // Ensure deep copy
      name: isRefinance ? null : originalLoan.name,
      id: undefined,  // Remove ID to avoid conflicts
      type: isRefinance ? EnumReportTypes.refinance : originalLoanType,
      isCloned: 1, // Mark it as cloned
      clonedLoanId: originalLoan.id,
      externalLoan: {
        ...originalLoan.externalLoan,
        loanId: undefined,
      }
    };

    if (isBorrower) {
      newLoans.push(newLoan);
    } else {
      newLoans.splice(key + 1, 0, newLoan);
    }

    form.setFieldValue('loans', newLoans);

    handleLoansUpdate?.({}, true);
  };

  const handleNameEditToggle = (index: number) => {
    setEditingName((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleAddressEditToggle = (index: number) => {
    setEditingAddress((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleAddressUpdate = (index: number) => {
    handleAddressEditToggle(index);
    handleLoansUpdate?.({}, true);
  };

  const handleNameUpdate = (index: number) => {
    handleNameEditToggle(index);
    handleLoansUpdate?.({}, true);

    getReportClosingCosts?.()
    // refetch closing costs
    setTimeout(() => {
      getLoans?.();
    }, 2000)
  };

  const updatePopoverNote = (key: number, name: string, value: string, isExternalLoans: boolean) => {
    form.setFieldValue(isExternalLoans ? ['loans', key, 'externalLoan', name] : ['loans', key, name], value);
    handleLoansUpdate?.();
  };

  const tooltipStyle = {
    backgroundColor: '#f5f5f5',
    fontSize: '12px',
  };

  return (
    <>
      <Col
        className={clsx(styles.tableCol, 'loan', { [styles.master]: field.key === 0 }, styles.width250)}
        ref={provided?.innerRef}
        {...provided?.draggableProps}
      >
        <Form.Item shouldUpdate>
          {() => {
            const tableView: IReportRow[] = form.getFieldValue('tableView');
            const col = form.getFieldValue(['loans', field.name]);
            const isFieldDisabled = col?.type !== 'borrower' && isBorrower;
            let name: string

            if (isRefinance && isFirst) {
              name = 'Current Loan';
            } else if (isRefinance) {
              const defaultName = `Refi Option ${field.name}`;

              name = form.getFieldValue(['loans', field.name, 'name']) || defaultName;
            } else {
              const defaultName = `Option ${field.name + 1}`;

              name = form.getFieldValue(['loans', field.name, 'name']) || defaultName;
            }

            const address = form.getFieldValue(['loans', field.name, 'address'])?.toUpperCase();
            let delimiter = '';

            if (address?.includes(';')) {
              delimiter = ';';
            } else if (address?.includes(',')) {
              delimiter = ',';
            }
            const address1 = delimiter ? address?.split(delimiter)[0] : address;
            const address2 = delimiter ? address?.replace(`${address1}${delimiter}`, '') : null;

            let isDragPopoverVisable = true;

            if (isFieldDisabled) {
              isDragPopoverVisable = false;
            }
            else if (isFirst && isRefinance) {
              isDragPopoverVisable = false
            }

            return (
              <>
                <div className={styles.tableHeadCell} style={{ backgroundColor: '#F5F5F9' }}>
                  <div className={styles.headerRow}>
                    {!editingName[field.name] ? (
                      <Tooltip placement="top" overlayStyle={tooltipStyle} title={name.toUpperCase()}>
                        <Typography.Title
                          level={4}
                          className={clsx(styles.title, 'color-gray8', {
                            [styles.disabled]: disabled,
                          })}
                          onClick={() => !isFieldDisabled && handleNameEditToggle(field.name)}
                        >
                          {name}
                        </Typography.Title>
                      </Tooltip>
                    ) : (
                      <Form.Item {...field} name={[field.name, 'name']} className="table-form-item">
                        <Input autoFocus onBlur={() => handleNameUpdate(field.name)} placeholder="Enter text" />
                      </Form.Item>
                    )}

                    {!disabled && (
                      <>
                        <div className={styles.headActions}>
                          {(!isFieldDisabled || isPreview) && (
                            <Popover content="Highlight column">
                              <Icon
                                component={Paint}
                                className={styles.icon}
                                onClick={() => colColorChange(field.name)}
                              />
                            </Popover>
                          )}
                          {!isBorrower && (
                            <Popover
                              content={
                                <div className={styles.popperContent}>
                                  <Typography.Title level={4}>Remove location</Typography.Title>
                                  <Typography className={styles.popperText}>
                                    To change your location, click here and select a new location
                                  </Typography>
                                </div>
                              }
                              trigger={isDragging ? '' : 'hover'}
                            >
                              {!isFieldDisabled && (
                                <Icon
                                  className={styles.icon}
                                  component={Delete}
                                  onClick={() =>
                                    openConfirm?.({
                                      title: 'Remove location',
                                      content: 'Are you sure you want to remove the location?',
                                      okText: 'Yes, remove',
                                      onOk: (handleClose) => {
                                        handleRemove();
                                        handleLoansUpdate?.(undefined, true);
                                        handleClose();
                                      },
                                    })
                                  }
                                />
                              )}
                            </Popover>
                          )}
                          <Popover content="Click to clone location" trigger={isDragging ? '' : 'hover'}>
                            <Icon className={styles.icon} component={Duplicate} onClick={() => handleClone(itemKey)} />
                          </Popover>
                        </div>
                        {isDragPopoverVisable && (
                          <Popover
                            content={
                              <div className={styles.popperContent}>
                                <Typography.Title level={4}>Drag to move</Typography.Title>
                                <Typography className={styles.popperText}>
                                  To make a column a master column, drag it to the first place
                                </Typography>
                              </div>
                            }
                            trigger={isDragging ? '' : 'hover'}
                          >
                            <Icon className={styles.icon} component={Drag} {...provided?.dragHandleProps} />
                          </Popover>
                        )}
                      </>
                    )}
                  </div>
                  {!editingAddress[field.name] ? (
                    <>
                      <Typography
                        style={{ cursor: 'pointer', whiteSpace: 'normal', fontSize: '0.825rem' }}
                        className={styles.location}
                        onClick={() => !isFieldDisabled && handleAddressEditToggle(field.name)}
                      >
                        {address1 ? `${address1}` : ''}
                      </Typography>
                      <Typography
                        style={{ cursor: 'pointer', whiteSpace: 'normal', fontSize: '0.825rem' }}
                        className={styles.location}
                        onClick={() => !isFieldDisabled && handleAddressEditToggle(field.name)}
                      >
                        {address2 || (!isFieldDisabled && '')}
                      </Typography>
                    </>
                  ) : (
                    <Form.Item {...field} name={[field.name, 'address']} className="table-form-item">
                      <Input autoFocus onBlur={() => handleAddressUpdate(field.name)} placeholder="Enter text" />
                    </Form.Item>
                  )}
                  {col?.type === 'borrower' && (
                    <div className={styles.added}>
                      <Popover
                        content={
                          <div className={styles.popperContent}>
                            <Typography className={styles.popperText}>
                              This column was added on {moment(col.createdAt).format('dddd, MM/DD/YYYY [at] hh:mm A')}
                            </Typography>
                          </div>
                        }
                        trigger={isDragging ? '' : 'hover'}
                      >
                        <Tag color={colorBlue} className="m-0 capitalize cursor-default">
                          Added
                        </Tag>{' '}
                      </Popover>
                    </div>
                  )}
                </div>
                <Form.Item noStyle name={['loans', field.name, 'id']} />
                {tableView &&
                  Object.values(tableView)
                    .filter((row) => row.active)
                    .sort((a, b) => a.order - b.order)
                    .map((row) => {
                      const loan = form.getFieldValue(['loans', field.name]);
                      const item = form.getFieldValue(['loans', field.name, row.name]);
                      const inputsCount = getInputsCount(tableView);
                      const isExternalLoans = Boolean(row?.isExternalLoans);
                      let rowStyle = { ...row.styles };

                      const shouldShowDash =
                        (
                          (
                            row.name === 'apr' ||
                            row.name === 'interestChange' ||
                            row.name === 'closingCosts'
                          ) && isRefinance && isFirst
                        ) ||
                        (
                          row.name === 'supplyTaxBill' && loan?.state !== 'CA'
                        ) ||
                        (row.name === 'closingCosts' && loan?.externalLoan?.closingCostsClearedAt)

                      if (row.name === 'total' && report?.enableComparisonDisplay) {
                        rowStyle = { ...rowStyle, border: '1px solid #dedede' };
                      }

                      if (row.color || loan.color || loan.loanColor?.[row.name]) {
                        rowStyle = {
                          ...rowStyle,
                          background: loan.loanColor?.[row.name] || row.color || loan.color,
                        };
                      }

                      const isShowSubField = row.subField && !(isBorrower && row.subField.isHiddenForBorrower) &&
                        (row.subField.name !== 'loanAmountWithUip' ||
                          (row.subField.name === 'loanAmountWithUip' &&
                            (
                              (loan.addUip && uipAvailable?.active) ||
                              (loan?.externalLoan?.addVaFundingFee && vaFundingFeeAvailable) ||
                              (loan?.externalLoan?.addClosingCosts && closingCostsAvailable && isRefinance) ||
                              (loan?.externalLoan?.addCashOut && cashOutAvailable)
                            ))
                            )

                      // eslint-disable-next-line @typescript-eslint/no-shadow
                      const getNoteValue = (loan: ILoanColumn, row: IReportRow, isExternalLoans: boolean) => {
                        if (isExternalLoans) {
                          // @ts-ignore - row.note is checked for undefined before use
                          return loan?.externalLoan?.[row.note || ''] === '0' ? '' : loan?.externalLoan?.[row.note]
                        }

                        // @ts-ignore - row.note is checked for undefined before use
                        return loan?.[row.note] === '0' ? '' : loan?.[row.note]
                      };

                      return (
                        <div key={`${field.key}-${row.name}`} className={styles.tableCell} style={rowStyle}>
                          {row.subName && <Form.Item noStyle name={['loans', field.name, row.subName]} />}
                          {shouldShowDash ? (
                            <div>-</div>
                          ) : (
                            <div className="loans-field-sub-field-containers">
                              {row.name === 'loanDetails' ? (
                                <div
                                  style={{
                                    minHeight: '60px',
                                    maxHeight: '65px',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  <Tooltip placement="top" overlayStyle={tooltipStyle} title={name.toUpperCase()}>
                                    <Typography.Title
                                      level={4}
                                      style={{
                                        marginBottom: '10px',
                                        color: '#8D94A2',
                                        fontWeight: '400',
                                      }}
                                    >
                                      {name.toUpperCase()}
                                    </Typography.Title>
                                  </Tooltip>
                                  <Typography
                                    style={{
                                      cursor: 'pointer',
                                      whiteSpace: 'normal',
                                      fontSize: '12px',
                                      color: '#8D94A2',
                                    }}
                                    className={styles.location}
                                    onClick={() => !isFieldDisabled && handleAddressEditToggle(field.name)}
                                  >
                                    {address?.replace(';', ' ,') || ''}
                                  </Typography>
                                </div>
                              ) : (
                                <Field
                                  row={row}
                                  field={field}
                                  loan={loan}
                                  loans={loans}
                                  form={form}
                                  isFieldDisabled={isFieldDisabled}
                                  handleLoansUpdate={handleLoansUpdate}
                                  inputsCount={inputsCount}
                                  disabled={disabled}
                                  reportType={reportType}
                                  itemKey={itemKey}
                                  report={report}
                                  item={item}
                                  filterLoansWithSegment={filterLoansWithSegment}
                                  isReadOnly={Boolean(loansLoading)}
                                  reportLoans={reportLoans}
                                  enableComparisonDisplay={report?.enableComparisonDisplay}
                                />
                              )}
                              {isShowSubField && (
                                <SubField
                                  row={row}
                                  field={field}
                                  loan={loan}
                                  loans={loans}
                                  form={form}
                                  isFieldDisabled={isFieldDisabled}
                                  handleLoansUpdate={handleLoansUpdate}
                                  inputsCount={inputsCount}
                                  disabled={disabled}
                                  reportType={reportType}
                                  itemKey={itemKey}
                                  report={report}
                                  isReadOnly={Boolean(loansLoading)}
                                  reportLoans={reportLoans}
                                />
                              )}
                            </div>
                          )}
                          {isPreview && (
                            <div
                              className={styles.actions}
                              style={
                                row.color || loan.color || loan.loanColor?.[row.name]
                                  ? { background: loan.loanColor?.[row.name] || row.color || loan.color }
                                  : undefined
                              }
                            >
                              <div className={styles.list}>
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '10px',
                                  }}
                                >
                                  <Popover content="Highlight cell">
                                    <Button
                                      type="ghost"
                                      icon={<Icon component={Paint} className={styles.icon} />}
                                      onClick={() => fieldColorChange(field.name, row.name)}
                                    />
                                  </Popover>
                                </div>
                              </div>
                            </div>
                          )}
                          {!disabled && !isFieldDisabled && (
                            <div
                              className={styles.actions}
                              style={
                                row.color || loan.color || loan.loanColor?.[row.name]
                                  ? { background: loan.loanColor?.[row.name] || row.color || loan.color }
                                  : undefined
                              }
                            >
                              <div className={styles.list}>
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '10px',
                                  }}
                                >
                                  {row.name === 'downPayment' && !isRefinance && (
                                    <Popover content="Remove Down Payment">
                                      <Checkbox
                                        onChange={(e) => onCheckboxRemoveDownPaymentToggle(e, loan.id)}
                                        defaultChecked={loan?.externalLoan?.removeDownPayment || false}
                                      />
                                    </Popover>
                                  )}
                                  {row.name === 'principalInterest' && (
                                    <Popover content="Change to interest only">
                                      <Checkbox
                                        onChange={(e) => onCheckboxInterestOnlyToggle(e, loan.id)}
                                        defaultChecked={loan?.interestOnly || false}
                                      />
                                    </Popover>
                                  )}
                                  {row.name === 'propertyTaxes' && loan.zpid && (
                                    <Popover content="Recalculate property taxes based on a refinance.">
                                      <Checkbox
                                        onChange={(e) => onCheckboxRecalculate(e, loan.id, loan.zpid)}
                                        defaultChecked={loan.recalculateTax || false}
                                      />
                                    </Popover>
                                  )}
                                  {row.name === 'uip' && (
                                    <Popover content="Add to loan amount.">
                                      <Checkbox
                                        onChange={(e) => onCheckAddUipToggle(e, loan.id)}
                                        checked={loan.addUip || false}
                                      />
                                    </Popover>
                                  )}
                                  {row.name === 'vaFundingFee' && (
                                    <Popover content="Add to loan amount.">
                                      <Checkbox
                                        onChange={(e) => onCheckAddVaFundingFeeToggle(e, loan.id)}
                                        checked={loan?.externalLoan?.addVaFundingFee || false}
                                      />
                                    </Popover>
                                  )}
                                  {
                                    row.name === 'closingCosts' &&
                                    isRefinance &&
                                    !isFirst &&
                                    loan?.type !== 'borrower' && (
                                    <Popover content="Add to loan amount.">
                                      <Checkbox
                                        onChange={(e) => onCheckAddClosingCostsToggle(e, loan.id)}
                                        checked={loan?.externalLoan?.addClosingCosts || false}
                                      />
                                    </Popover>
                                  )}
                                  {row.name === 'cashOut' && (
                                    <Popover content="Add to loan amount.">
                                      <Checkbox
                                        onChange={(e) => onCheckAddCashOutToggle(e, loan.id)}
                                        checked={loan?.externalLoan?.addCashOut || false}
                                      />
                                    </Popover>
                                  )}
                                  {!isBorrower && row.note && row.name !== 'total' && (
                                    <Popover
                                      content={
                                        <div style={{ maxWidth: 230 }}>
                                          <Typography.Text>Note for {row.text?.toLowerCase()} field</Typography.Text>
                                          <Input
                                            autoFocus
                                            defaultValue={getNoteValue(loan, row, isExternalLoans)}
                                            onBlur={(e) =>
                                              updatePopoverNote(
                                                itemKey,
                                                row.note || '',
                                                e.target.value,
                                                isExternalLoans
                                              )}
                                          />
                                        </div>
                                      }
                                      trigger="click"
                                    >
                                      <Tooltip title="Add Note" overlayStyle={tooltipStyle}>
                                        <Button type="ghost" icon={<Icon component={Note} className={styles.icon} />} />
                                      </Tooltip>
                                    </Popover>
                                  )}
                                  <Popover content="Highlight cell">
                                    <Button
                                      type="ghost"
                                      icon={<Icon component={Paint} className={styles.icon} />}
                                      onClick={() => fieldColorChange(field.name, row.name)}
                                    />
                                  </Popover>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
              </>
            );
          }}
        </Form.Item>
      </Col>
      {/* <LoanClosingCosts open={closingCostsModal} onCancel={() => setClosingCostsModal(false)} loanId={loanId} /> */}
    </>
  );
};

TableCol.defaultProps = {
  provided: undefined,
};

export default TableCol;
